import type {FC, ReactNode} from 'react'
import {createContext, useContext, useEffect, useMemo, useRef} from 'react'
import {routePageContext} from './routeFallback'
import {sanitizePageContext} from './sanitize'
import type {PageContextV1, PublishedPageContext} from './types'

type PageContextRegistry = {
  /** Register what a page knows; returns the function that withdraws it. */
  publish: (ctx: PublishedPageContext) => () => void
  /** The current context: route fallback, the latest published context over it, sanitized. */
  read: (overrides?: PublishedPageContext) => PageContextV1
}

const PageContextRegistryCtx = createContext<PageContextRegistry | null>(null)

/** `over` without its null and undefined members, so they never mask `base`. */
const defined = <T extends object>(over: T | undefined): Partial<T> =>
  Object.fromEntries(
    Object.entries(over ?? {}).filter(([, value]) => value !== null && value !== undefined),
  ) as Partial<T>

export const mergePageContext = (
  base: PublishedPageContext,
  over: PublishedPageContext | undefined,
): PublishedPageContext => {
  if (!over) return base
  const timeRange = defined(over.time_range)
  return {
    page: over.page ?? base.page,
    entities: {...base.entities, ...defined(over.entities)},
    // A window is one value: a page that knows its window replaces the URL's.
    time_range: timeRange.start || timeRange.end ? over.time_range : base.time_range,
    view: {...base.view, ...defined(over.view)},
  }
}

type Props = {
  children: ReactNode
}

/**
 * Holds what the user has open, for the Alpinist side panel to attach to each
 * message (AB#6762). Lives in a ref, not in state: pages publish on every change
 * of their view and nothing re-renders for it — it is read only when a message is
 * sent or the screen is captured.
 */
export const PageContextProvider: FC<Props> = ({children}) => {
  // Insertion-ordered: the most recently mounted publisher wins.
  const publishedRef = useRef(new Map<symbol, PublishedPageContext>())

  const registry = useMemo<PageContextRegistry>(
    () => ({
      publish: (ctx) => {
        const token = Symbol('page-context')
        publishedRef.current.set(token, ctx)
        return () => {
          publishedRef.current.delete(token)
        }
      },
      read: (overrides) => {
        const {pathname, search} = window.location
        const published = Array.from(publishedRef.current.values()).pop()
        const merged = mergePageContext(
          mergePageContext(routePageContext(pathname, search), published),
          overrides,
        )
        return sanitizePageContext({
          ...merged,
          v: 1,
          page: merged.page ?? 'other',
          path: pathname,
          captured_at: new Date().toISOString(),
        })
      },
    }),
    [],
  )

  return (
    <PageContextRegistryCtx.Provider value={registry}>{children}</PageContextRegistryCtx.Provider>
  )
}

/** The registry, or null outside DashTemplate (the public demo embed, tests). */
export const usePageContextRegistry = (): PageContextRegistry | null =>
  useContext(PageContextRegistryCtx)

/**
 * Publish what this page shows, for Alpinist. Pass references only (ids, the
 * time window, view params). Re-publishes when the serialized context changes;
 * withdrawn on unmount, when the route fallback applies again.
 */
export const usePublishPageContext = (ctx: PublishedPageContext | null): void => {
  const registry = usePageContextRegistry()
  const key = ctx ? JSON.stringify(ctx) : null
  useEffect(() => {
    if (!registry || !key) return undefined
    return registry.publish(JSON.parse(key) as PublishedPageContext)
  }, [registry, key])
}
